<i18n
  src="@/locales/views/agro_exporter/certifications/providers/show_organization.yml"
  lang="yaml"
></i18n>
<i18n src="@/locales/models/organization_certification.yml" lang="yaml"></i18n>

<template>
  <go-back-link
    :text="$t('labels.go_back_link')"
    :name="routeName('list_providers')"
    :params="{
      agro_exporter_id: agro_exporter_id
    }"
  ></go-back-link>
  <bread-crumbs :labels="[module_name, organization?.name]"></bread-crumbs>
  <template v-if="load_data_general">
    <div class="flex flex-row justify-end">
      <div class="panel-plain-label">
        <badge-default
          :label="
            organization_certifications.length +
            ' ' +
            $t('labels.total_number_organization_certifications')
          "
        />
      </div>
      <div class="grow"></div>
      <new-button
        v-if="organization.manage_certifications"
        @click.prevent="goToNewOrganizationCertifications()"
        :label="$t('labels.new_organization_certifications_button')"
      />
    </div>
    <div class="mt-8 flex flex-col">
      <alert-danger :errors_data="std_err" />
      <div class="table-first-container">
        <div class="table-second-container">
          <div class="table-third-container">
            <table class="table">
              <thead class="table-header">
                <tr>
                  <th scope="col" class="table-header-cell">
                    {{
                      $t("model.organization_certification.certification_id")
                    }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{
                      $t("model.organization_certification.certification_code")
                    }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.organization_certification.renewal_date") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.organization_certification.expiration_date") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.organization_certification.active") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    {{ $t("model.organization_certification.expired_at") }}
                  </th>
                  <th scope="col" class="table-header-cell">
                    <i class="fa-solid fa-paperclip"></i>
                  </th>
                  <th
                    scope="col"
                    class="table-header-cell"
                    v-if="organization.manage_certifications"
                  ></th>
                </tr>
              </thead>
              <tbody class="table-body">
                <tr
                  class="table-row"
                  v-for="organization_certification in organization_certifications"
                  :key="organization_certification.id"
                >
                  <td class="table-cell">
                    {{ organization_certification.certification.name }}
                  </td>
                  <td class="table-cell">
                    {{ organization_certification.certification_code }}
                  </td>
                  <td class="table-cell">
                    {{ dateFormatter(organization_certification.renewal_date) }}
                  </td>
                  <td class="table-cell">
                    {{
                      dateFormatter(organization_certification.expiration_date)
                    }}
                  </td>
                  <td class="table-cell">
                    <yes-no-label :value="organization_certification.active" />
                  </td>
                  <td class="table-cell">
                    <div
                      v-if="organization_certification.active"
                      v-html="expiredAt(organization_certification)"
                    ></div>
                  </td>
                  <td class="table-cell">
                    <i
                      v-if="organization_certification.certificate_filename"
                      :class="[
                        'fa-solid',
                        organization_certification.downloading_certificate
                          ? 'fa-sync fa-spin'
                          : 'fa-download'
                      ]"
                      @click.prevent="
                        downloadCertificateFile(organization_certification)
                      "
                    ></i>
                  </td>
                  <td
                    v-if="organization.manage_certifications"
                    class="table-cell-action"
                  >
                    <destroy-button
                      v-if="!organization_certification.proc_button_destroy"
                      :disabled_button="false"
                      @destroyEvent="
                        destroyOrganizationCertification(
                          organization_certification
                        )
                      "
                      :label="$t('labels.destroy_button_label')"
                      :title="
                        $t('labels.destroy_button_title', {
                          certification_name:
                            organization_certification.certification.name
                        })
                      "
                      :message="$t('labels.destroy_button_message')"
                      :label_confirm="$t('labels.destroy_button_label_confirm')"
                    />
                    <destroy-button-processing
                      v-else
                      :label="$t('labels.destroy_button_label')"
                    />
                    <span class="space-actions"></span>
                    <edit-button
                      @click.prevent="
                        goToEditOrganizationCertification(
                          organization_certification.id
                        )
                      "
                      :label="$t('labels.edit_button')"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<script>
import Requester from "@/views/agro_exporter/requesters/certifications/providers/manage_organization_certification.js";

export default {
  name: "ShowOrganization",
  data() {
    return {
      load_data_general: false,
      proc_button_destroy: false,
      organization: {},
      organization_certifications: []
    };
  },
  props: {
    agro_exporter_id: { required: true },
    organization_id: { required: true }
  },
  created() {
    this.checkIfAuthenticated().then(() =>
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          agro_exporter_id: this.agro_exporter_id
        }
      }).then(() => this.loadData())
    );
  },
  methods: {
    async loadData() {
      await this.fetchOrganization();
      await this.fetchOrganizationCertifications();
      this.load_data_general = true;
    },
    async fetchOrganization() {
      let response = await Requester.fetchOrganization(
        this.agro_exporter_id,
        this.organization_id
      );

      this.organization = response.data;
    },
    async fetchOrganizationCertifications() {
      let response = await Requester.fetchOrganizationCertifications(
        this.agro_exporter_id,
        this.organization_id
      );

      this.organization_certifications = response.data;
      this.organization_certifications.forEach(org_certif => {
        org_certif["proc_button_destroy"] = false;
        org_certif["downloading_certificate"] = false;
      });
    },
    async downloadCertificateFile(organization_certification) {
      organization_certification.downloading_certificate = true;
      try {
        var response = await Requester.downloadCertificateFile(
          this.agro_exporter_id,
          this.organization_id,
          organization_certification.id
        );
        const file = response.data;
        var fileURL = window.URL.createObjectURL(new Blob([file]));
        var fileLink = document.createElement("a");

        fileLink.href = fileURL;
        fileLink.setAttribute(
          "download",
          organization_certification.certificate_filename
        );
        document.body.appendChild(fileLink);

        fileLink.click();
      } catch (error) {
        error;
      }
      organization_certification.downloading_certificate = false;
    },
    async destroyOrganizationCertification(organization_certification) {
      organization_certification.proc_button_destroy = true;
      try {
        let response = await Requester.destroy(
          this.agro_exporter_id,
          this.organization_id,
          organization_certification.id
        );
        this.registerSuccessNotification({
          title: response.data.success_message
        });
        this.organization_certifications.splice(
          this.organization_certifications.indexOf(organization_certification),
          1
        );
      } catch (error) {
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title
          });
        }
      }
      organization_certification.proc_button_destroy = false;
    },
    expiredAt(organization_certification) {
        let days = this.daysUntilExpirationDate(
          organization_certification.expiration_date
        );

        if (days > 0) {
          return `<span>
            <i class="fa-solid fa-circle-check text-green-500"></i>
            ${this.$t("labels.days", { days: this.numberWithPrecision(days) })}
            </span>`;
        } else {
          return `
            <span class="text-red-500">
            <i class="fa-solid fa-triangle-exclamation text-red-500"></i>
            ${this.$t("labels.expired_date")}
            </span>`;
        }
    },
    goToNewOrganizationCertifications() {
      this.$router.push({
        name: this.routeName("new_organization_certification"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          organization_id: this.organization_id
        }
      });
    },
    goToEditOrganizationCertification(organization_certification_id) {
      this.$router.push({
        name: this.routeName("edit_organization_certification"),
        params: {
          agro_exporter_id: this.agro_exporter_id,
          organization_id: this.organization_id,
          organization_certification_id: organization_certification_id
        }
      });
    }
  }
};
</script>
